/**
 * WorkspaceSwitcher
 *
 * The workspace section of the avatar menu (Figma 9073:1436): every
 * workspace the user belongs to as a `DropdownItem` row — logo or initials,
 * name, check on the active one — followed by "+ Add New Workspace".  The
 * list is shown even with a single workspace so the current context is
 * always visible.  Picking another row calls POST /workspaces/{id}/switch
 * and reloads so every cached view starts from the new workspace; the add
 * button hands off to the owner, which opens `CreateWorkspaceModal`.
 */
import React, { useState } from 'react';
import { Avatar, Button, DropdownItem } from '@spark';
import { useAuth } from '@/features/auth/AuthContext';
import './WorkspaceSwitcher.css';

/** Workspace logo (data URL set in onboarding / Settings) or an initials fallback. */
export function WorkspaceAvatar({ workspace, size = 'small' }) {
    const name = workspace?.name || 'Workspace';
    if (workspace?.logo) {
        return <Avatar size={size} type="image" src={workspace.logo} alt="" className="workspace-switcher__avatar" />;
    }
    return <Avatar size={size} type="initials" primaryText={name} className="workspace-switcher__avatar" />;
}

const reloadPage = () => window.location.reload();

export default function WorkspaceSwitcher({ onAddWorkspace, reload = reloadPage }) {
    const { workspace, workspaces, switchWorkspace } = useAuth();
    const [switching, setSwitching] = useState(false);
    const list = Array.isArray(workspaces) ? workspaces : [];

    const handleSwitch = async (wsId) => {
        if (wsId === workspace?.id || switching) return;
        setSwitching(true);
        try {
            await switchWorkspace(wsId);
            reload();
        } catch (err) {
            console.error('Workspace switch failed:', err);
            setSwitching(false);
        }
    };

    return (
        <div className="workspace-switcher" aria-busy={switching || undefined}>
            {list.length > 0 ? (
                <div className="workspace-switcher__list" role="group" aria-label="Workspaces">
                    {list.map((ws) => {
                        const isActive = ws.id === workspace?.id;
                        let state = 'default';
                        if (isActive) state = 'selected';
                        else if (switching) state = 'disabled';
                        return (
                            <DropdownItem
                                key={ws.id}
                                id={`workspace-switcher-${ws.id}`}
                                className="workspace-switcher__item"
                                label={ws.name}
                                prefix={<WorkspaceAvatar workspace={ws} />}
                                state={state}
                                onClick={() => handleSwitch(ws.id)}
                            />
                        );
                    })}
                </div>
            ) : null}
            <Button
                type="button"
                variant="ghost"
                size="tiny"
                fill
                showStartIcon
                className="workspace-switcher__add"
                disabled={switching}
                onClick={() => onAddWorkspace?.()}
            >
                Add New Workspace
            </Button>
        </div>
    );
}
